import React, { useEffect, useState } from 'react';
import { arrayOf, bool, func, node, oneOfType, shape, string } from 'prop-types';
import { equals } from 'ramda';

import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import ListItemButton from '@mui/material/ListItemButton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import {
  CheckBox,
  CheckBoxOutlineBlank,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
} from '@mui/icons-material';

const styles = {
  chip: {
    backgroundColor: 'primary.light',
    borderWidth: '1px',
    fontSize: '12px',
    height: 'unset',
    mb: 0.5,
    mr: 0.5,
    px: 0,
    py: 0.25,
  },
  filterInput: {
    '.MuiChip-root': {
      borderColor: 'secondary.main',
    },
    '> .MuiOutlinedInput-input': {
      minWidth: '0 !important',
    },
    display: 'flex',
    flexWrap: 'nowrap !important',
    pr: '0 !important',
  },
  filterPanel: {
    '.MuiAutocomplete-popperDisablePortal': {
      '> *': {
        backgroundColor: 'transparent',
        borderRadius: 0,
        boxShadow: 'none',
      },
      height: '40vh',
      m: '0 !important',
      position: 'unset !important',
      transform: 'unset !important',
      width: '100%',
    },
  },
  limitTag: {
    backgroundColor: 'secondary.main',
    color: 'surface.white',
  },
  popper: {
    '.MuiAutocomplete-option': {
      '&[aria-selected="true"]': {
        svg: {
          '&.checked': { display: 'inline-block' },
          '&.unchecked': { display: 'none' },
          color: 'secondary.main',
        },
      },
      svg: {
        '&.checked': { display: 'none' },
        '&.unchecked': { display: 'inline-block' },
      },
    },
  },
};

const CollapsibleFilter = ({ children, label, ...props }) => {
  const [expanded, setExpanded] = useState(true);

  return (
    <Box {...props}>
      <ListItemButton
        onClick={() => setExpanded(b => !b)}
        sx={{ alignItems: 'center', display: 'flex', justifyContent: 'space-between' }}
      >
        <Typography variant="label">{label}</Typography>
        <Box sx={{ color: 'primary.main' }}>
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </Box>
      </ListItemButton>
      <Collapse in={expanded} sx={styles.filterPanel}>
        {children}
      </Collapse>
    </Box>
  );
};

CollapsibleFilter.propTypes = {
  children: node.isRequired,
  label: node.isRequired,
};

const MAX_TAGS = 1;

const TerminalLocatorFilter = ({
  collapsible,
  collapsibleLabel,
  label,
  multiple,
  onApply,
  open: alwaysOpen,
  optionLabel,
  options,
  selectAllLabel,
  value: appliedValue,
  ...props
}) => {
  const [open, setOpen] = useState(null);
  const [selection, setSelection] = useState(multiple ? [] : null);

  useEffect(() => {
    appliedValue && setSelection(appliedValue);
  }, [appliedValue]);

  const isSelectAll = s => s[optionLabel] === 'Select All';

  const handleChangeFilter = (event, value) => {
    if (!selectAllLabel) {
      setSelection(value);
      return;
    }
    const selectAllNext = value.some(isSelectAll);

    if (selection.some(isSelectAll)) {
      setSelection(!selectAllNext ? [] : value.filter(s => !isSelectAll(s)));
    } else {
      setSelection(selectAllNext || value.length === options.length - 1 ? options : value);
    }
  };

  const render = () => (
    <Autocomplete
      disablePortal={alwaysOpen}
      getOptionLabel={({ [optionLabel]: ol }) => ol}
      limitTags={MAX_TAGS}
      multiple={multiple}
      onBlur={() => setOpen(false)}
      onChange={handleChangeFilter}
      onFocus={() => setOpen(true)}
      open={alwaysOpen || open}
      options={options}
      renderInput={rProps => (
        <TextField
          {...rProps}
          label={label}
          slotProps={{
            input: {
              ...rProps.InputProps,
              endAdornment: (
                <IconButton
                  disabled={
                    equals(selection, appliedValue) ||
                    (!selection && (!multiple || !appliedValue)) ||
                    (multiple &&
                      selection.length === 0 &&
                      (!appliedValue || appliedValue.length === 0))
                  }
                  onClick={event => {
                    event.stopPropagation();
                    onApply(selection);
                  }}
                  sx={{
                    '&:hover': {
                      backgroundColor:
                        equals(selection, appliedValue) ||
                        (!selection && (!multiple || !appliedValue)) ||
                        (multiple &&
                          selection.length === 0 &&
                          (!appliedValue || appliedValue.length === 0))
                          ? 'transparent'
                          : 'primary.dark',
                      color:
                        equals(selection, appliedValue) ||
                        (!selection && (!multiple || !appliedValue)) ||
                        (multiple &&
                          selection.length === 0 &&
                          (!appliedValue || appliedValue.length === 0))
                          ? 'text.disabled'
                          : 'surface.white',
                    },
                    backgroundColor:
                      equals(selection, appliedValue) ||
                      (!selection && (!multiple || !appliedValue)) ||
                      (multiple &&
                        selection.length === 0 &&
                        (!appliedValue || appliedValue.length === 0))
                        ? 'transparent'
                        : 'primary.main',
                    border: 0,
                    borderRadius: 0.5,
                    color:
                      equals(selection, appliedValue) ||
                      (!selection && (!multiple || !appliedValue)) ||
                      (multiple &&
                        selection.length === 0 &&
                        (!appliedValue || appliedValue.length === 0))
                        ? 'text.disabled'
                        : 'surface.white',
                    mx: 1,
                  }}
                >
                  <SearchIcon size={20} />
                </IconButton>
              ),
              sx: styles.filterInput,
            },
          }}
        />
      )}
      renderOption={
        !multiple
          ? null
          : ({ key, ...renderProps }) => (
              <Box component="li" key={key} {...renderProps}>
                <CheckBoxOutlineBlank className="unchecked" size={40} />
                <CheckBox className="checked" size={40} />
                <Typography sx={{ ml: 2 }}>{key}</Typography>
              </Box>
            )
      }
      renderTags={(tags, getTagProps) =>
        selection.find(s => s[optionLabel] === 'Select All') ? (
          <Chip label={selectAllLabel} variant="outlined" />
        ) : (
          <>
            {tags.slice(0, MAX_TAGS).map((tag, index) => (
              <Chip {...getTagProps({ index })} label={tag[optionLabel]} variant="outlined" />
            ))}
            {MAX_TAGS > 0 && tags.length > MAX_TAGS && (
              <Chip label={`+${tags.length - MAX_TAGS}`} sx={styles.limitTag} variant="outlined" />
            )}
          </>
        )
      }
      slotProps={{ popper: { placement: 'bottom-start', sx: styles.popper } }}
      value={selection}
      {...props}
    />
  );

  return collapsible ? (
    <CollapsibleFilter
      label={
        <>
          {collapsibleLabel}&nbsp;
          {!!selection.length && (
            <Chip
              label={selection.filter(s => s[optionLabel] !== 'Select All').length}
              sx={styles.chip}
            />
          )}
        </>
      }
    >
      {render()}
    </CollapsibleFilter>
  ) : (
    render()
  );
};

TerminalLocatorFilter.propTypes = {
  collapsible: bool,
  collapsibleLabel: string,
  label: string,
  multiple: bool,
  onApply: func.isRequired,
  open: bool,
  optionLabel: string.isRequired,
  options: arrayOf(shape({})),
  selectAllLabel: string,
  value: oneOfType([shape({}), arrayOf(shape({}))]),
};

TerminalLocatorFilter.defaultProps = {
  collapsible: false,
  collapsibleLabel: 'Filter',
  label: 'Filter',
  multiple: false,
  open: false,
  options: [],
  selectAllLabel: null,
  value: null,
};

export default TerminalLocatorFilter;
